import React, { useMemo, useState } from "react";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";

import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
} from "@mui/material";

/**
 * Firebase (ajuste os exports se necessário)
 */
import { useAuthContext } from "../../contexts/AuthContext";

const steps = ["Organizador", "Evento", "Vendas & Financeiro", "Revisão"];

const eventTypes = [
  "Festa / Balada",
  "Show",
  "Festival",
  "Corporativo",
  "Congresso / Palestra",
  "Esportivo",
  "Outro",
];

const organizerTypes = [
  "Pessoa física",
  "Empresa / Produtora",
  "Coletivo / Agência",
];

const audienceRanges = ["Até 100", "100–300", "300–800", "800–2.000", "2.000+"];
const priceRanges = [
  "Até R$ 30",
  "R$ 30–60",
  "R$ 60–120",
  "R$ 120–250",
  "R$ 250+",
];

function onlyDigits(v = "") {
  return String(v).replace(/\D/g, "");
}

function formatPhoneBR(value = "") {
  const d = onlyDigits(value).slice(0, 11);
  if (!d) return "";
  const ddd = d.slice(0, 2);
  const p1 = d.slice(2, 7);
  const p2 = d.slice(7, 11);
  if (d.length <= 2) return `(${ddd}`;
  if (d.length <= 7) return `(${ddd}) ${d.slice(2)}`;
  return `(${ddd}) ${p1}${p2 ? `-${p2}` : ""}`;
}

function isValidEmail(v = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
}

export default function OrganizerSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerOrganizer } = useAuthContext();

  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [data, setData] = useState({
    // STEP 1 - organizador
    organizerName: "",
    organizerEmail: "",
    organizerPhone: "",
    organizerCityState: "",
    organizerPassword: "",
    organizerPasswordConfirm: "",
    organizerType: "Pessoa física",

    // STEP 2 - evento
    eventName: "",
    eventType: "Festa / Balada",
    eventCityState: "",
    eventDate: "", // yyyy-mm-dd
    eventFrequency: "Único",

    // STEP 3 - vendas & financeiro
    alreadySells: "Não, é meu primeiro evento",
    currentPlatform: "",
    audienceRange: "100–300",
    priceRange: "R$ 60–120",
    wantsAdvance: "Talvez",

    // STEP 4 - termos
    acceptTerms: false,
  });

  const updateLocal = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const requiredByStep = useMemo(() => {
    if (activeStep === 0) {
      return [
        ["organizerName", "Informe seu nome/empresa"],
        ["organizerEmail", "Informe um e-mail válido"],
        ["organizerPhone", "Informe um WhatsApp"],
        ["organizerCityState", "Informe cidade/estado"],
      ];
    }
    if (activeStep === 1) {
      return [
        ["eventName", "Informe o nome do evento"],
        ["eventCityState", "Informe cidade/estado do evento"],
        ["eventDate", "Informe a data (ou prevista)"],
      ];
    }
    if (activeStep === 2) {
      return [];
    }
    if (activeStep === 3) {
      return [["acceptTerms", "Você precisa aceitar os termos para enviar"]];
    }
    return [];
  }, [activeStep]);

  const validateStep = () => {
    setFormError("");

    for (const [key, msg] of requiredByStep) {
      const v = data[key];

      if (key === "organizerEmail") {
        if (!isValidEmail(v)) {
          setFormError(msg);
          return false;
        }
        continue;
      }

      if (key === "organizerPhone") {
        const d = onlyDigits(v);
        if (d.length < 10) {
          setFormError(msg);
          return false;
        }
        continue;
      }

      if (key === "acceptTerms") {
        if (!v) {
          setFormError(msg);
          return false;
        }
        continue;
      }

      if (!String(v || "").trim()) {
        setFormError(msg);
        return false;
      }
    }

    // Validação de senha no step 0
    if (activeStep === 0) {
      if (!data.organizerPassword || data.organizerPassword.length < 8) {
        setFormError("A senha deve ter pelo menos 8 caracteres.");
        return false;
      }
      if (data.organizerPassword !== data.organizerPasswordConfirm) {
        setFormError("As senhas não conferem.");
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setFormError("");
    setActiveStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    setFormError("");

    try {
      const { uid } = await registerOrganizer(data, data.organizerPassword);
      navigate("/organizadores/solicitacao-enviada", { state: { requestId: uid } });
    } catch (e) {
      console.error(e);
      let msg = "Nao foi possivel enviar agora. Verifique sua conexao e tente novamente.";
      if (e?.code === "auth/email-already-in-use") {
        msg = "Este e-mail ja esta cadastrado. Faca login ou use outro e-mail.";
      } else if (e?.code === "auth/weak-password") {
        msg = "Senha muito fraca. Use pelo menos 8 caracteres.";
      }
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at top center, rgba(0,180,255,0.12), transparent 60%), radial-gradient(900px 500px at 10% 30%, rgba(120,90,255,0.10), transparent 55%), #040912",
        display: "flex",
        alignItems: "center",
        py: { xs: 5, md: 9 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: { xs: "18px", md: "24px" },
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(18px)",
            border: "1px solid rgba(255,255,255,0.12)",
            overflow: "hidden",
          }}
        >
          <Stack spacing={2.2}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  Para organizadores
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  Cadastro de Organizador
                </Typography>
                <Typography sx={{ mt: 0.7, color: "rgba(255,255,255,0.72)" }}>
                  Em poucos passos você envia seus dados e a gente libera seu
                  painel.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label="ONURB PASS"
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,180,255,0.16)",
                    color: "rgba(255,255,255,0.85)",
                    border: "1px solid rgba(0,180,255,0.25)",
                  }}
                />
                <Chip
                  label="ONURB FINANCE"
                  size="small"
                  sx={{
                    bgcolor: "rgba(120,90,255,0.14)",
                    color: "rgba(255,255,255,0.85)",
                    border: "1px solid rgba(120,90,255,0.22)",
                  }}
                />
              </Stack>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

            {/* Stepper */}
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label": { color: "rgba(255,255,255,0.7)" },
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#fff",
                  fontWeight: 700,
                },
                "& .MuiStepLabel-label.Mui-completed": {
                  color: "rgba(255,255,255,0.9)",
                },
                "& .MuiStepIcon-root": { color: "rgba(255,255,255,0.25)" },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "rgba(0,180,255,0.85)",
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "rgba(0,180,255,0.65)",
                },
                "& .MuiStepConnector-line": {
                  borderColor: "rgba(255,255,255,0.10)",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {formError ? (
              <Alert
                severity="warning"
                sx={{ bgcolor: "rgba(255,193,7,0.12)", color: "#fff" }}
              >
                {formError}
              </Alert>
            ) : null}

            {/* Content */}
            <Box sx={{ mt: 1 }}>
              {activeStep === 0 && (
                <Stack spacing={2}>
                  <Typography sx={{ fontWeight: 800 }}>Quem é você?</Typography>

                  <TextField
                    label="Nome completo / Razão social"
                    fullWidth
                    value={data.organizerName}
                    onChange={(e) =>
                      updateLocal({ organizerName: e.target.value })
                    }
                  />

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="E-mail"
                      type="email"
                      fullWidth
                      value={data.organizerEmail}
                      onChange={(e) =>
                        updateLocal({ organizerEmail: e.target.value })
                      }
                    />
                    <TextField
                      label="WhatsApp"
                      fullWidth
                      value={data.organizerPhone}
                      onChange={(e) =>
                        updateLocal({
                          organizerPhone: formatPhoneBR(e.target.value),
                        })
                      }
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Cidade / Estado"
                      fullWidth
                      placeholder="Ex: Aracaju - SE"
                      value={data.organizerCityState}
                      onChange={(e) =>
                        updateLocal({ organizerCityState: e.target.value })
                      }
                    />

                    <TextField
                      label="Tipo de organizador"
                      select
                      fullWidth
                      value={data.organizerType}
                      onChange={(e) =>
                        updateLocal({ organizerType: e.target.value })
                      }
                    >
                      {organizerTypes.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <Divider sx={{ borderColor: "rgba(255,255,255,0.10)", my: 0.5 }} />

                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    Crie sua senha de acesso
                  </Typography>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Senha"
                      type="password"
                      fullWidth
                      value={data.organizerPassword}
                      onChange={(e) =>
                        updateLocal({ organizerPassword: e.target.value })
                      }
                      helperText="Minimo 8 caracteres"
                    />
                    <TextField
                      label="Confirmar senha"
                      type="password"
                      fullWidth
                      value={data.organizerPasswordConfirm}
                      onChange={(e) =>
                        updateLocal({ organizerPasswordConfirm: e.target.value })
                      }
                    />
                  </Stack>

                  <Typography
                    sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}
                  >
                    Dica: voce pode comecar como PF agora e depois completar os
                    dados da empresa.
                  </Typography>
                </Stack>
              )}

              {activeStep === 1 && (
                <Stack spacing={2}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Sobre o evento
                  </Typography>

                  <TextField
                    label="Nome do evento"
                    fullWidth
                    value={data.eventName}
                    onChange={(e) => updateLocal({ eventName: e.target.value })}
                  />

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Tipo de evento"
                      select
                      fullWidth
                      value={data.eventType}
                      onChange={(e) =>
                        updateLocal({ eventType: e.target.value })
                      }
                    >
                      {eventTypes.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Evento"
                      select
                      fullWidth
                      value={data.eventFrequency}
                      onChange={(e) =>
                        updateLocal({ eventFrequency: e.target.value })
                      }
                    >
                      {["Único", "Recorrente"].map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Cidade / Estado do evento"
                      fullWidth
                      placeholder="Ex: Salvador - BA"
                      value={data.eventCityState}
                      onChange={(e) =>
                        updateLocal({ eventCityState: e.target.value })
                      }
                    />
                    <TextField
                      label="Data do evento"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={data.eventDate}
                      onChange={(e) =>
                        updateLocal({ eventDate: e.target.value })
                      }
                    />
                  </Stack>

                  <Typography
                    sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}
                  >
                    Se a data ainda não está fechada, coloque uma data estimada
                    — ajustamos depois.
                  </Typography>
                </Stack>
              )}

              {activeStep === 2 && (
                <Stack spacing={2}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Vendas & Financeiro
                  </Typography>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Você já vende ingressos hoje?"
                      select
                      fullWidth
                      value={data.alreadySells}
                      onChange={(e) =>
                        updateLocal({ alreadySells: e.target.value })
                      }
                    >
                      {[
                        "Não, é meu primeiro evento",
                        "Sim, em outra plataforma",
                        "Sim, venda própria",
                      ].map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Se sim, qual plataforma?"
                      fullWidth
                      disabled={
                        data.alreadySells === "Não, é meu primeiro evento"
                      }
                      value={data.currentPlatform}
                      onChange={(e) =>
                        updateLocal({ currentPlatform: e.target.value })
                      }
                      placeholder="Ex: Sympla, IngressoLive..."
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Público estimado"
                      select
                      fullWidth
                      value={data.audienceRange}
                      onChange={(e) =>
                        updateLocal({ audienceRange: e.target.value })
                      }
                    >
                      {audienceRanges.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Faixa de preço do ingresso"
                      select
                      fullWidth
                      value={data.priceRange}
                      onChange={(e) =>
                        updateLocal({ priceRange: e.target.value })
                      }
                    >
                      {priceRanges.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <TextField
                    label="Pretende antecipação (ONURB Finance)?"
                    select
                    fullWidth
                    value={data.wantsAdvance}
                    onChange={(e) =>
                      updateLocal({ wantsAdvance: e.target.value })
                    }
                  >
                    {["Sim", "Talvez", "Não"].map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Typography
                    sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}
                  >
                    Isso não é aprovação automática — é só pra entender a
                    operação e liberar limites iniciais com segurança.
                  </Typography>
                </Stack>
              )}

              {activeStep === 3 && (
                <Stack spacing={2}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Revisão final
                  </Typography>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.2,
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography
                        sx={{ fontWeight: 800, color: "rgba(255,255,255,0.9)" }}
                      >
                        Organizador
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
                        <b>Nome:</b> {data.organizerName || "—"}
                        <br />
                        <b>E-mail:</b> {data.organizerEmail || "—"}
                        <br />
                        <b>WhatsApp:</b> {data.organizerPhone || "—"}
                        <br />
                        <b>Cidade/UF:</b> {data.organizerCityState || "—"}
                        <br />
                        <b>Tipo:</b> {data.organizerType || "—"}
                      </Typography>

                      <Divider
                        sx={{ borderColor: "rgba(255,255,255,0.10)", my: 1 }}
                      />

                      <Typography
                        sx={{ fontWeight: 800, color: "rgba(255,255,255,0.9)" }}
                      >
                        Evento
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
                        <b>Nome:</b> {data.eventName || "—"}
                        <br />
                        <b>Tipo:</b> {data.eventType || "—"}
                        <br />
                        <b>Cidade/UF:</b> {data.eventCityState || "—"}
                        <br />
                        <b>Data:</b> {data.eventDate || "—"}
                        <br />
                        <b>Frequência:</b> {data.eventFrequency || "—"}
                      </Typography>

                      <Divider
                        sx={{ borderColor: "rgba(255,255,255,0.10)", my: 1 }}
                      />

                      <Typography
                        sx={{ fontWeight: 800, color: "rgba(255,255,255,0.9)" }}
                      >
                        Vendas & Financeiro
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
                        <b>Já vende hoje:</b> {data.alreadySells || "—"}
                        <br />
                        <b>Plataforma:</b> {data.currentPlatform || "—"}
                        <br />
                        <b>Público:</b> {data.audienceRange || "—"}
                        <br />
                        <b>Preço:</b> {data.priceRange || "—"}
                        <br />
                        <b>Antecipação:</b> {data.wantsAdvance || "—"}
                      </Typography>
                    </Stack>
                  </Paper>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={data.acceptTerms}
                        onChange={(e) =>
                          updateLocal({ acceptTerms: e.target.checked })
                        }
                      />
                    }
                    label={
                      <Typography
                        sx={{ color: "rgba(255,255,255,0.78)", fontSize: 14 }}
                      >
                        Li e aceito os{" "}
                        <RouterLink
                          to="/termos"
                          style={{
                            color: "inherit",
                            textDecoration: "underline",
                          }}
                        >
                          Termos de Uso
                        </RouterLink>{" "}
                        e a{" "}
                        <RouterLink
                          to="/privacidade"
                          style={{
                            color: "inherit",
                            textDecoration: "underline",
                          }}
                        >
                          Política de Privacidade
                        </RouterLink>
                        .
                      </Typography>
                    }
                  />

                  <Typography
                    sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}
                  >
                    Ao enviar, seu cadastro vai para <b>review</b> com status{" "}
                    <b>pending</b>, para você aprovar no admin.
                  </Typography>
                </Stack>
              )}
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={() => navigate("/organizadores")}
                variant="text"
                sx={{ color: "rgba(255,255,255,0.70)" }}
              >
                ← Voltar
              </Button>

              <Stack direction="row" spacing={1.2} alignItems="center">
                <Button
                  disabled={activeStep === 0 || submitting}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(255,255,255,0.22)",
                    color: "rgba(255,255,255,0.86)",
                    borderRadius: "14px",
                    px: 2,
                  }}
                >
                  Voltar etapa
                </Button>

                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    sx={{
                      borderRadius: "14px",
                      fontWeight: 800,
                      px: 2.4,
                      py: 1.2,
                    }}
                  >
                    Próximo
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    variant="contained"
                    sx={{
                      borderRadius: "14px",
                      fontWeight: 900,
                      px: 2.4,
                      py: 1.2,
                    }}
                  >
                    {submitting ? "Enviando..." : "Enviar para análise"}
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>

        <Box textAlign="center" mt={3}>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
            ONURB Pass • Área do Organizador • 2026
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
